import { useEffect, useState } from "react";
import { Upload } from "lucide-react";
import toast from "react-hot-toast";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label } from "../../../components/ui";
import { Textarea } from "../../../components/ui/textarea";
import { uploadComplianceDocument } from "../api/complianceApi";

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Upload a document against one compliance requirement. The file is named
 * automatically by the backend ("<property id> - Property Price Statement - V2 - date.pdf")
 * and filed into the Documents tab + Google Drive like any other upload.
 */
export default function UploadComplianceDocDialog({ open, propertyId, item, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [executedAt, setExecutedAt] = useState(todayISO());
  const [note, setNote] = useState("");
  const [indicativeLow, setIndicativeLow] = useState("");
  const [indicativeHigh, setIndicativeHigh] = useState("");
  const [markCurrent, setMarkCurrent] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null); setExecutedAt(todayISO()); setNote("");
      setIndicativeLow(""); setIndicativeHigh(""); setMarkCurrent(false);
    }
  }, [open, item?.requirement_key]);

  if (!item) return null;
  const isPps = item.requirement_key === "pps";
  const reviewable = !!item.reviewable;

  const dateLabel = isPps ? "Date prepared" : item.requirement_key === "sales_authority" ? "Date signed" : "Document date";

  const submit = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setSaving(true);
    try {
      const extra = {};
      if (isPps) {
        if (indicativeLow) extra.indicative_low = indicativeLow;
        if (indicativeHigh) extra.indicative_high = indicativeHigh;
        extra.date_prepared = executedAt;
      }
      const res = await uploadComplianceDocument(propertyId, {
        requirementKey: item.requirement_key, file, executedAt, note,
        status: reviewable ? (markCurrent ? "current" : "received") : undefined,
        extra,
      });
      toast.success(`${item.label}: ${res.data?.display_name || "uploaded"}`);
      onUploaded?.(res.data);
      onClose?.();
    } catch (err) {
      const d = err?.response?.data;
      toast.error(d?.detail || (d && Object.values(d).flat().join(" ")) || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose?.(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload: {item.label}</DialogTitle>
          <DialogDescription>
            {isPps
              ? "A new PPS version. Previous versions are kept (never overwritten); this one becomes the main statement on REA, Domain and the portal."
              : reviewable
                ? "Lands as Received. Mark it reviewed / current once checked so it can be sent to purchasers."
                : "Files into the property's Documents tab and Google Drive, named automatically."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>File (PDF)</Label>
            <Input type="file" accept="application/pdf,image/*,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div>
            <Label>{dateLabel}</Label>
            <Input type="date" value={executedAt} onChange={(e) => setExecutedAt(e.target.value)} />
          </div>
          {isPps && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Indicative price from</Label>
                <Input inputMode="numeric" placeholder="e.g. 1,000,000" value={indicativeLow} onChange={(e) => setIndicativeLow(e.target.value)} />
              </div>
              <div>
                <Label>Indicative price to</Label>
                <Input inputMode="numeric" placeholder="e.g. 1,100,000" value={indicativeHigh} onChange={(e) => setIndicativeHigh(e.target.value)} />
              </div>
            </div>
          )}
          {reviewable && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={markCurrent} onChange={(e) => setMarkCurrent(e.target.checked)} />
              I have reviewed this package; mark it current (available to purchasers)
            </label>
          )}
          <div>
            <Label>Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. updated Section 32 from the vendor's conveyancer" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !file}>
            <Upload className="h-4 w-4 mr-2" />{saving ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
